import { Card, Icon, InfoTooltip } from '@ps/isp-web-component-library';
import { BetBuilder } from '@ps/isp-web-component-library/icons/BetBuilder';
import type {
  InvalidSgmOpportunity,
  MultipleBetType,
  SgmOpportunity as SgmOpportunityType
} from '@ps/isp-web-services-types';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import {
  getIsAvailableOpportunity,
  getIsRestrictedPlayMoneyOpportunity,
  getIsSgmInvalidOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import { select } from '@swf/core/store';

import { SgmEventAndLegCount } from '../../../../../SgmEventAndLegCount/SgmEventAndLegCount';
import { SgmOpportunityDescription } from '../../../../../SgmOpportunityDescription/SgmOpportunityDescription';
import BetBuilderError from '../BetBuilderError/BetBuilderError';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import OddsDisplay from '../Heading/components/OddsDisplay/OddsDisplay';
import MultiplesTooltipContent from '../MultiplesTooltipContent/MultiplesTooltipContent';
import PotentialReturn from '../PotentialReturn/PotentialReturn';
import PromotionPicker from '../PromotionPicker/PromotionPicker';
import Stake from '../Stake/Stake';
import StakeAlerts from '../StakeAlerts/StakeAlerts';

import {
  TXTWCL_Bet_Builder,
  TXTWCL_Close,
  TXTWCL_Multiple_bet_information
} from './SgmOpportunity.messages.json';
import styles from './SgmOpportunity.scss';

interface Props {
  opportunity: SgmOpportunityType | InvalidSgmOpportunity;
}

const SgmOpportunity: React.FC<Props> = ({ opportunity }) => {
  const { formatMessage } = useRegulatoryIntl();
  const opportunityRef = React.createRef<HTMLDivElement>();
  const isPromotionPickerEnabled = useFeature(FeatureFlag.ISPPromotionPicker);
  const selectedPromotionPicker = useSelector(select.betSlip.selectedPromotionPicker);

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSgmInvalidOpportunity = getIsSgmInvalidOpportunity(opportunity);
  const isRestrictedPlayMoneyOpportunity = getIsRestrictedPlayMoneyOpportunity(opportunity);

  return (
    <Card data-testid="bet-slip-sgm-opportunity" ref={opportunityRef}>
      <div className={styles.wrapper}>
        <div>
          <div className={styles.iconWithHeadingWrapper}>
            <Icon icon={BetBuilder} size="xl" />
            <div className={styles.headingWrapper}>
              <div className={styles.heading}>
                <div>
                  <h4
                    className={classNames(styles.name, {
                      [styles.suspended]: !isAvailableOpportunity && !isSgmInvalidOpportunity
                    })}
                  >
                    <Translation {...TXTWCL_Bet_Builder} />
                  </h4>

                  <InfoTooltip
                    parentRef={opportunityRef as React.RefObject<HTMLDivElement>}
                    closeText={formatMessage(TXTWCL_Close)}
                    labelText={formatMessage(TXTWCL_Multiple_bet_information)}
                    isDisabled={Boolean(selectedPromotionPicker)}
                  >
                    <MultiplesTooltipContent betType={opportunity.type as MultipleBetType} isSgm />
                  </InfoTooltip>
                </div>

                {isAvailableOpportunity && <OddsDisplay opportunity={opportunity} />}
              </div>

              <SgmEventAndLegCount sgmData={opportunity.sgmData} />
            </div>
          </div>

          {opportunity.sgmData.length && (
            <SgmOpportunityDescription
              isRestrictedPlayMoneyOpportunity={isRestrictedPlayMoneyOpportunity}
              sgmData={opportunity.sgmData}
            />
          )}

          <div className={styles.stakeWrapper}>
            {!isSgmInvalidOpportunity && (
              <Stake className={styles.stake} opportunity={opportunity} />
            )}

            {isAvailableOpportunity && <PotentialReturn opportunity={opportunity} />}
          </div>
        </div>

        {isPromotionPickerEnabled && <PromotionPicker opportunity={opportunity} />}

        {isAvailableOpportunity && <StakeAlerts opportunity={opportunity} />}

        {isSgmInvalidOpportunity && <BetBuilderError failureCode={opportunity.failureCode} />}
      </div>
      <ErrorBanner fieldName={opportunity.id} />
    </Card>
  );
};

export default SgmOpportunity;
